import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import NoAppointments from './NoAppointments';
import AppointmentView from './AppointmentView';
import {
  PracticeOnly,
  ProviderAndPractice,
  SchedulingConfig,
  ViewProps,
} from './common';

import { ReactComponent as AlertCircle } from '../../../assets/alert-circle.svg';
import { ReactComponent as CalendarSvg } from '../../../assets/calendar.svg';

import * as api from '../../../api';

import styles from '../index.module.css';
import ProviderInfo from './ProviderView/ProviderInfo';
import PracticeInfo from './PracticeView/PracticeInfo';
import { Typography } from '@vivaahealth/design-system';
import Filters, { Props as FilterProps } from '../Filters';

const MultiView: React.FC<
  ViewProps & {
    // Should make fieldConfig scheduleAppointmentDisplayBy not nullable https://app.asana.com/0/1201935264203449/1202660659147310
    scheduleAppointmentDisplayBy: api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy;
    filterProps?: FilterProps;
  }
> = ({
  availableEhrTimeSlots,
  onAppointmentSelected,
  fieldConfig,
  practices,
  providers,
  scheduleAppointmentDisplayBy,
  patientOrder,
  mainProviderUserId,
  patientPcpPreferred,
  filterProps,
}) => {
  const [uniqueConfigIds, setUniqueConfigIds] = useState<
    Pick<api.EhrTimeSlot.EhrTimeSlot, 'practiceId' | 'providerId'>[]
  >([]);
  const [mainProviderHasSlots, setMainProviderHasSlots] =
    useState<boolean>(true);
  const [mainPracticeId, setMainPracticeId] = useState<string>('');
  const mainProviderId =
    (patientOrder?.referredToProvider?.id || mainProviderUserId) ?? null;
  useEffect(() => {
    const uniqueSlotConfigs = _.uniqBy(availableEhrTimeSlots, (slot) => {
      return JSON.stringify(_.pick(slot, ['providerId', 'practiceId']));
    });
    if (mainProviderId) {
      setMainProviderHasSlots(
        uniqueSlotConfigs
          .map((slot) => slot.providerId)
          .includes(mainProviderId),
      );
      setMainPracticeId(uniqueSlotConfigs[0].practiceId);
    }
    setUniqueConfigIds(
      uniqueSlotConfigs.map((slot) =>
        _.pick(slot, ['providerId', 'practiceId']),
      ),
    );
  }, [availableEhrTimeSlots, practices, mainProviderId]);
  const providerAndPractice = useMemo(():
    | ProviderAndPractice[]
    | PracticeOnly[] => {
    const providerAndPractices: ProviderAndPractice[] | PracticeOnly[] =
      uniqueConfigIds.map((tuple) => {
        const { providerId, practiceId } = tuple;
        const slot = availableEhrTimeSlots.find(
          (slot) =>
            slot.providerId === providerId && slot.practiceId === practiceId,
        )!;
        const practice = practices[slot.practiceId];
        const provider = slot.providerId
          ? providers[slot.providerId]
          : undefined;
        if (provider) {
          const result: ProviderAndPractice = {
            ...slot,
            ...provider,
            ...practice,
            practiceId: slot.practiceId,
            providerUserId: slot.providerId!,
            specialties: provider.specialties ?? null,
          };
          return result;
        }
        const result: PracticeOnly = {
          ...slot,
          ...practice,
        };
        return result;
      });
    return scheduleAppointmentDisplayBy ===
      api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy.Practice
      ? _.uniqBy(providerAndPractices, 'practiceId')
      : providerAndPractices;
  }, [
    practices,
    providers,
    scheduleAppointmentDisplayBy,
    uniqueConfigIds,
    availableEhrTimeSlots,
  ]);

  const getMainProviderAndPractice = (): ProviderAndPractice | undefined => {
    const mainProvider = mainProviderId ? providers[mainProviderId] : undefined;
    const mainPractice = practices[mainPracticeId];
    if (
      mainProvider &&
      scheduleAppointmentDisplayBy ===
        api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy
          .Provider
    ) {
      return {
        ..._.pick(
          mainProvider,
          'firstName',
          'lastName',
          'prefix',
          'suffix',
          'previsitImageUrl',
          'phoneNumber',
        ),
        ...mainPractice,
        practiceId: mainPracticeId,
        providerUserId: mainProviderId!,
        specialties: mainProvider.specialties ?? null,
      };
    }
    return;
  };

  if (providerAndPractice.length === 0) {
    return (
      <NoAppointments
        className={styles.MultiProviderNoAppointmentContent}
        fieldConfig={fieldConfig}
      />
    );
  }

  return !patientOrder?.alternateReferredToProvider && !patientPcpPreferred ? (
    <div className={styles.MultiProviderContainer}>
      {providerAndPractice.map((config) => {
        const distance = availableEhrTimeSlots.find(
          (slot) => slot.practiceId === config.practiceId,
        )?.distance;
        return (
          <div
            key={`${config.practiceId}${
              'providerUserId' in config ? `,${config.providerUserId}` : ''
            }`}
          >
            <div className={styles.MultiProviderListItemDivider}></div>
            <div className={styles.MultiProviderListItem}>
              <div className={styles.MultiProviderItem}>
                {'providerUserId' in config &&
                scheduleAppointmentDisplayBy ===
                  api.PrevisitFormTemplateFieldConfig
                    .ScheduleAppointmentDisplayBy.Provider ? (
                  <ProviderInfo config={config} distance={distance} />
                ) : (
                  <PracticeInfo config={config} distance={distance} />
                )}
                <div className={styles.MultiProviderAppointmentContainer}>
                  <AppointmentView
                    timeSlots={availableEhrTimeSlots}
                    config={config}
                    onAppointmentSelected={onAppointmentSelected}
                    fieldConfig={fieldConfig}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  ) : (
    <AlternateProviderView
      mainProviderHasSlots={mainProviderHasSlots}
      getMainProviderAndPractice={getMainProviderAndPractice}
      patientOrder={patientOrder}
      providerAndPractice={providerAndPractice}
      availableEhrTimeSlots={availableEhrTimeSlots}
      mainProviderId={mainProviderId}
      scheduleAppointmentDisplayBy={scheduleAppointmentDisplayBy}
      onAppointmentSelected={onAppointmentSelected}
      fieldConfig={fieldConfig}
      filterProps={filterProps}
    />
  );
};

export default MultiView;

const AlternatesInfoPanel: React.FC<{
  mainProviderHasSlots: boolean;
  hasPatientOrderAlternateProvider: boolean;
}> = ({ mainProviderHasSlots, hasPatientOrderAlternateProvider }) => {
  const { t } = useTranslation();
  const message = !hasPatientOrderAlternateProvider
    ? 'These other providers are also available.'
    : mainProviderHasSlots
      ? "Your provider has designated the below alternate if the above availability doesn't fit your schedule."
      : "Since your provider has no upcoming availability, they've designated the below provider as an alternate so that you can schedule sooner.";
  return (
    <div
      className={styles.AlternatesInfoPanel}
      aria-label="Alternate provider info panel"
    >
      <AlertCircle className={styles.AlternatesInfoPanelIcon} />
      <Typography variant="title-medium" className="my-auto">
        {t(message)}
      </Typography>
    </div>
  );
};

const AlternateProviderView: React.FC<{
  mainProviderHasSlots: boolean;
  getMainProviderAndPractice: () => ProviderAndPractice | undefined;
  patientOrder?: api.EhrTimeSlot.EhrTimeSlotPatientOrder;
  providerAndPractice: ProviderAndPractice[] | PracticeOnly[];
  availableEhrTimeSlots: api.EhrTimeSlot.EhrTimeSlot[];
  mainProviderId: string | null;
  scheduleAppointmentDisplayBy: api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy;
  onAppointmentSelected: (appointment: api.EhrTimeSlot.EhrTimeSlot) => void;
  fieldConfig: SchedulingConfig;
  patientPcpPreferred?: boolean;
  filterProps?: FilterProps;
}> = ({
  mainProviderHasSlots,
  getMainProviderAndPractice,
  patientOrder,
  providerAndPractice,
  availableEhrTimeSlots,
  mainProviderId,
  scheduleAppointmentDisplayBy,
  onAppointmentSelected,
  fieldConfig,
  patientPcpPreferred,
  filterProps,
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.AlternateProviderContainer}>
      {getMainProviderAndPractice() !== undefined && !mainProviderHasSlots && (
        <div className={styles.AlternateProviderListItem}>
          <div className={styles.AlternateProviderItem}>
            <ProviderInfo
              config={getMainProviderAndPractice()!}
              hasAlternateProvider={
                !!patientOrder?.alternateReferredToProvider ||
                providerAndPractice.length > 1
              }
            />
            <div className={styles.NoAppointmentsMessageContainer}>
              <CalendarSvg
                viewBox="0 0 28 24"
                className={styles.ProviderCalendarIcon}
              />
              <Typography variant="paragraph-medium" bold>
                {t('No appointments available')}
              </Typography>
            </div>
          </div>
        </div>
      )}
      {providerAndPractice.map((config, i) => {
        const distance = availableEhrTimeSlots.find(
          (slot) => slot.practiceId === config.practiceId,
        )?.distance;
        return (
          'providerUserId' in config && (
            <div key={`${config.practiceId}${config.providerUserId}`}>
              <div className={styles.AlternateProviderListItem}>
                {config.providerUserId !== mainProviderId && i === 1 && (
                  <>
                    <AlternatesInfoPanel
                      mainProviderHasSlots={mainProviderHasSlots}
                      hasPatientOrderAlternateProvider={
                        !!patientOrder?.alternateReferredToProvider
                      }
                    />
                    {!!filterProps && (
                      <Filters
                        onFiltersChange={filterProps.onFiltersChange}
                        filters={filterProps.filters}
                        displayBy={scheduleAppointmentDisplayBy ?? undefined}
                        daysAllowedFrom={filterProps.daysAllowedFrom}
                        daysAllowedTo={filterProps.daysAllowedTo}
                        sortProviderAlternatesOnly={true}
                        patientPcpPreferred={patientPcpPreferred}
                      />
                    )}
                  </>
                )}
                <div className={styles.AlternateProviderItem}>
                  {scheduleAppointmentDisplayBy ===
                  api.PrevisitFormTemplateFieldConfig
                    .ScheduleAppointmentDisplayBy.Provider ? (
                    <ProviderInfo
                      config={config}
                      distance={distance}
                      hasAlternateProvider={
                        !!patientOrder?.alternateReferredToProvider ||
                        providerAndPractice.length > 1
                      }
                    />
                  ) : (
                    <PracticeInfo config={config} distance={distance} />
                  )}
                  <div className={styles.AlternateProviderAppointmentContainer}>
                    <AppointmentView
                      timeSlots={availableEhrTimeSlots}
                      config={config}
                      onAppointmentSelected={onAppointmentSelected}
                      fieldConfig={fieldConfig}
                      patientOrder={patientOrder}
                    />
                  </div>
                </div>
                {i === providerAndPractice.length - 1 && (
                  <div
                    className={styles.AlternateProviderListItemDivider}
                  ></div>
                )}
              </div>
            </div>
          )
        );
      })}
    </div>
  );
};
